<template>
  <div class="login-root">

    <!-- ── Top bar ── -->
    <header class="login-topbar">
      <div class="login-brand">
        <img :src="seguraLogotype" class="login-logotype" alt="Segura Watch" />
      </div>
      <nav class="login-nav">
        <button @click="handleLogin" :disabled="isLoading" class="login-topbar-btn">
          {{ isLoading ? 'Signing in…' : 'Get started' }}
        </button>
      </nav>
    </header>

    <!-- ── Background symbol ── -->
    <div class="login-bg-symbol" aria-hidden="true">
      <img :src="seguraIconDeep" alt="" />
    </div>

    <!-- ── Main content ── -->
    <main class="login-main">
      <div class="login-inner">

        <p class="login-eyebrow">PAM Session Intelligence · Early access</p>

        <h1 class="login-headline">
          Every privileged<br>session, fully<br>understood.
        </h1>

        <p class="login-sub">
          Behavioral detection, instant alerts, automated verdicts
          and natural language investigation across every privileged session.
        </p>

        <div class="login-cta">
          <button @click="handleLogin" :disabled="isLoading" class="login-google-btn">
            <span class="google-icon-wrap">
              <Loader2 v-if="isLoading" class="btn-spin" />
              <svg v-else viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </span>
            Continue with Google
          </button>

          <p v-if="errorMsg" class="login-error">{{ errorMsg }}</p>

          <p class="login-legal">
            By continuing, you agree to our
            <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
          </p>
        </div>

      </div>
    </main>

  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { Loader2 } from "lucide-vue-next";
import { loginWithGoogle } from "../services/authService";
import { useAuthStore } from "../stores/authStore";
import seguraLogotype from "../assets/segura-logotype_deep-green.svg";
import seguraIconDeep from "../assets/segura-icon-deep.svg";

const router    = useRouter();
const authStore = useAuthStore();
const isLoading = ref(false);
const errorMsg  = ref("");

async function handleLogin() {
  isLoading.value = true;
  errorMsg.value  = "";
  try {
    const user = await loginWithGoogle();
    if (user) { authStore.setUser(user); router.replace("/"); }
  } catch (error) {
    console.error("[Login] Failed:", error);
    errorMsg.value  = "Authentication failed. Please try again.";
    isLoading.value = false;
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Stix+Two+Text:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
</style>

<style scoped>
/* ── Root ─────────────────────────────────────────── */
.login-root {
  min-height: 100vh;
  background: #fdf8f1;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

/* ── Top bar ──────────────────────────────────────── */
.login-topbar {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 2rem;
  background: rgba(253,248,241,0.85);
  border-bottom: 1px solid rgba(0,58,50,0.08);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.login-brand {
  display: flex;
  align-items: center;
}
.login-logotype {
  height: 1.375rem;
  width: auto;
  object-fit: contain;
}

.login-nav {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.login-nav-link {
  font-size: 0.8125rem;
  color: #006b69;
  text-decoration: none;
  font-weight: 500;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.login-nav-link:hover { opacity: 1; }

.login-topbar-btn {
  padding: 0.4375rem 1.125rem;
  background: #003a32;
  color: #fdf8f1;
  border: none;
  border-radius: 980px;
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  letter-spacing: 0.01em;
}
.login-topbar-btn:hover:not(:disabled) { background: #006b69; }
.login-topbar-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Background symbol ────────────────────────────── */
.login-bg-symbol {
  position: absolute;
  right: -8%;
  bottom: -5%;
  width: 65vw;
  max-width: 900px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.04;
}
.login-bg-symbol img { width: 100%; height: auto; }

/* ── Main content ─────────────────────────────────── */
.login-main {
  position: relative;
  z-index: 10;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4rem 2rem 4rem 10vw;
}

.login-inner {
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.login-eyebrow {
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #00d1b5;
  margin: 0 0 2rem 0;
}

.login-headline {
  font-family: 'Stix Two Text', 'Georgia', serif;
  font-size: clamp(2.75rem, 5vw, 4.25rem);
  font-weight: 600;
  line-height: 1.08;
  letter-spacing: -0.02em;
  color: #003a32;
  margin: 0 0 1.5rem 0;
}

.login-sub {
  font-size: 1.0625rem;
  line-height: 1.65;
  color: #4a6360;
  font-weight: 400;
  margin: 0 0 3rem 0;
}

.login-cta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
}

.login-google-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.625rem;
  background: #fff;
  border: 1.5px solid rgba(0,58,50,0.14);
  border-radius: 980px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #003a32;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,58,50,0.08), 0 1px 2px rgba(0,0,0,0.04);
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
}
.login-google-btn:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(0,58,50,0.12), 0 1px 4px rgba(0,0,0,0.06);
  border-color: rgba(0,209,181,0.3);
  transform: translateY(-1px);
}
.login-google-btn:active:not(:disabled) { transform: scale(0.99); }
.login-google-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.google-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.btn-spin { width: 18px; height: 18px; animation: spin 0.8s linear infinite; }

.login-error {
  font-size: 0.8125rem;
  color: #ff905f;
  margin: 0;
}

.login-legal {
  font-size: 0.75rem;
  color: #8aada9;
  margin: 0;
  line-height: 1.6;
}
.login-legal a { color: #006b69; text-decoration: underline; text-underline-offset: 2px; }
.login-legal a:hover { color: #003a32; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 640px) {
  .login-main { padding: 3rem 1.5rem; justify-content: center; }
  .login-inner { max-width: 100%; }
  .login-cta { align-items: stretch; }
  .login-google-btn { justify-content: center; }
  .login-bg-symbol { right: -20%; bottom: 0; width: 100vw; }
}
</style>